"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import ObituaryCard from "@/app/components/appcomponents/ObituaryCard";
import { buildCardData } from "@/app/components/appcomponents/thankYouCard";

// Look of the box, chosen by the author: cream (green heading, on the old yellow paper of the memory page
// Posvetilo slides) or navy (gold heading).
export const BACKGROUND_STYLES = {
  cream: {
    box: "bg-[#F9EFDE] bg-[url('/memory_paper_slider.jpg')] bg-cover bg-center",
    cardBorder: "#248864",
    headingColor: "text-[#248864]",
    text: "text-[#1E2125]",
    divider: "bg-[#D4D4D4]",
    meta: "text-[#7C7C7C]",
    open: "text-[#0A85C2]",
  },
  navy: {
    box: "bg-[#1F2937]",
    cardBorder: "#FCD34D",
    headingColor: "text-[#FCD34D]",
    text: "text-[#F3F4F6]",
    divider: "bg-[#6B7280]",
    meta: "text-[#9CA3AF]",
    open: "text-white",
  },
};

/**
 * One message box: the standard obituary card on top, then the heading and text.
 * `inCarousel`: the box as it sits in the memory page's Posvetilo slider - the same box without the obituary
 * card on top (the visitor is already on that page) and without the footer.
 * `message.alignment` ("left" | "center") and `message.background` ("cream" | "navy") are chosen by the author.
 */
export default function ThankYouMessageCard({ message, onCandleBurned, hideOpen = false, inCarousel = false }) {
  const router = useRouter();
  const style = BACKGROUND_STYLES[message.background] ?? BACKGROUND_STYLES.cream;
  const obituary = message.Obituary;
  const cardData = buildCardData(message);
  const centered = message.alignment === "center";
  const memoryHref = !inCarousel && obituary?.slugKey ? `/m/${obituary.slugKey}` : null;

  return (
    <article
      // The whole box opens the memory page, except where it covers links and buttons of its own.
      onClick={(e) => {
        if (memoryHref && !e.target.closest("a, button")) router.push(memoryHref);
      }}
      className={`${memoryHref ? "cursor-pointer " : ""}relative w-full ${inCarousel ? "" : "max-w-[660px] "}rounded-[2px] shadow-[0_4px_14px_rgba(0,0,0,0.18)] px-4 min-[743px]:px-[80px] ${inCarousel ? "pt-[53px] pb-[60px]" : "pt-[53px] pb-[18px]"} ${style.box}`}
      aria-label={`${message.headline}: ${obituary?.name ?? ""} ${obituary?.sirName ?? ""}`}
    >
      {/* Phones (up to 742px): the box grows up to 500px, the text only until the 420px screen and then stops. */}
      <div className={`mx-auto w-full ${inCarousel ? "max-w-[525px] mobile:max-w-[368px]" : "max-w-[500px] mobile:max-w-[364px]"}`}>
        {!inCarousel && (
          <>
            <ObituaryCard
              data={cardData}
              datesText={cardData.datesText}
              index={0}
              mob={false}
              variant="message"
              borderColor={style.cardBorder}
              onCandleBurned={onCandleBurned}
            />

            <div className={`mx-auto mt-[40px] h-px w-[298px] max-w-full ${style.divider}`} />
          </>
        )}

        <h2
          className={`${inCarousel ? "" : "mt-[48px] "}font-greatVibes text-[48px] leading-[48px] font-normal ${style.headingColor} ${
            centered ? "text-center" : "text-left"
          }`}
        >
          {message.headline}
        </h2>

        <p
          className={`mt-[24px] whitespace-pre-wrap break-words text-[16px] leading-6 font-normal font-variation-customOpt16 ${style.text} ${
            centered ? "text-center" : "text-left"
          }`}
        >
          {message.message}
        </p>
      </div>

      {!inCarousel && (
        <div className="mx-auto mt-[28px] flex w-full max-w-[500px] items-center justify-between text-[14px]">
          <span className={`text-[12px] ${style.meta}`}>
            {message.sample ? message.sampleLabel : ""}
          </span>
          {memoryHref && !hideOpen && (
            <Link
              href={memoryHref}
              className={`flex items-center gap-1 ${style.open}`}
              aria-label={`Odpri spominsko stran: ${obituary.name} ${obituary.sirName}`}
            >
              Odpri
              <span aria-hidden="true" className="text-[20px] leading-none">
                ›
              </span>
            </Link>
          )}
        </div>
      )}
    </article>
  );
}
